/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { HallowAnalyticsEvent } from "@packages/analytics";
import {
  useRequestCommunitiesAdminCommunity,
  useRequestMe,
} from "@packages/sdk";
import {
  CommunitySelector,
  ExitIcon,
  ReportedCounter,
  SidebarTab,
  spacing,
} from "@packages/ui";
import { useParams } from "next/navigation";

import { getBasePADUrl, PAD_TABS } from "../../lib";
import { GivingStripeConnectSidebar } from "../parishAdminDashboard/giving/GivingStripeConnectButton/GivingStripeConnectSidebar";

const menuBarLinksContainerStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  gap: spacing.ms,
};

export const MenuBarDashboardMobileChildren = () => {
  const params = useParams<{ communityId: string }>();
  const { query: communityQuery } = useRequestCommunitiesAdminCommunity({
    id: parseInt(params.communityId),
  });
  const { query: meQuery } = useRequestMe();

  const parishAdminBaseUrl = getBasePADUrl({
    communityId: parseInt(params.communityId),
  });

  return (
    <>
      <CommunitySelector triggerState="expanded" />
      <div css={menuBarLinksContainerStyles}>
        {PAD_TABS.map(
          ({
            sidebarState,
            path,
            Icon,
            getHidden,
            subTabs,
            message,
            getMessage,
            ...props
          }) => (
            <SidebarTab
              key={path}
              href={`${parishAdminBaseUrl}/${path}`}
              state={"expanded"}
              message={message ?? getMessage(communityQuery.data)}
              analyticsEvent={{
                event: HallowAnalyticsEvent.TappedAdminDashboardTab,
                properties: {
                  tab: path,
                },
              }}
              actionRight={
                (path === "reported" && (
                  <ReportedCounter communityId={parseInt(params.communityId)} />
                )) ||
                (path === "giving" && (
                  <GivingStripeConnectSidebar
                    communityId={parseInt(params.communityId)}
                  />
                ))
              }
              icon={<Icon fill="currentColor" />}
              hidden={
                getHidden
                  ? communityQuery.data && meQuery.data
                    ? getHidden({
                        community: communityQuery.data,
                        user: meQuery.data,
                      })
                    : true
                  : false
              }
              subTabs={subTabs?.map((subTab) => ({
                analyticsEvent:
                  path === "settings"
                    ? {
                        event: HallowAnalyticsEvent.TappedCommunitySettings,
                        properties: {
                          option: subTab.path.replace("-", "_"),
                        },
                      }
                    : {
                        event: HallowAnalyticsEvent.TappedAdminDashboardTab,
                        properties: {
                          tab: subTab.path,
                        },
                      },
                href: `${parishAdminBaseUrl}/${path}/${subTab.path}`,
                icon: <subTab.Icon fill="currentColor" />,
                getHidden: subTab.getHidden
                  ? communityQuery.data && meQuery.data
                    ? getHidden({
                        community: communityQuery.data,
                        user: meQuery.data,
                      })
                    : true
                  : false,
                message: subTab.message,
              }))}
              {...props}
            />
          ),
        )}
      </div>
      <SidebarTab
        href="/home"
        icon={<ExitIcon />}
        message="web_exit_dashboard"
        state="expanded"
      />
    </>
  );
};
